/**
 * HandlingPanel (13-26): the investigation-and-actions stage (FIELDSET §3.4) on all five registers.
 *
 * - **The record's handling columns** through `VerbForm` with `verb = 'handling'` (13-20's key; on Risk it is the
 *   treat verb, D-13-20-11): Incidents open disclosure and the findings; Complaints withdrawal, the target resolution
 *   date, the Commissioner route, the advocate and open disclosure; Feedback withdrawal and the Commissioner route;
 *   Risk the treatment, the residual rating and, for a WHS hazard, the resolution dates. Attachments are not inputs
 *   (D-13-L-6). CI has no handling column but its attachments, so it gets the log only.
 * - **Recording a withdrawal** asks once more in the confirm: the record stays open until it is closed or cancelled.
 * - **The log** below (`SiblingComposer`): actions, investigation steps and the §3.4.1 rows, each its own sibling row.
 */

import VerbForm from '../VerbForm';
import SiblingComposer from '../SiblingComposer';
import { fieldOf } from '../../../config/fieldset/registerFields';
import { verbInputColumns } from '../../../lib/verbForm';
import type { RecordPatch } from '../../../types/records';
import type { PanelProps } from './registry';
import styles from '../record.module.css';

/** FIELDSET §3.4: the handling column that records a withdrawal (Complaints, Feedback). */
const WITHDRAWN_ON = 'WithdrawnOn';
if (!fieldOf('complaints', WITHDRAWN_ON) || !fieldOf('feedback', WITHDRAWN_ON)) {
  throw new Error(`HandlingPanel: ${WITHDRAWN_ON} is not a Complaints and Feedback column`);
}

function withdrawalNote(patch: RecordPatch) {
  if (!(WITHDRAWN_ON in patch) || patch[WITHDRAWN_ON] === null) return null;
  return (
    <p className={styles.confirmNote}>
      <strong>This records a withdrawal.</strong> The record stays open until you close or cancel it.
    </p>
  );
}

export default function HandlingPanel({ row, siblings, actor, onSaved }: PanelProps) {
  const register = row.register;
  const columns = verbInputColumns(register, 'handling');

  return (
    <div className={styles.verbForm}>
      {columns.length > 0 ? (
        <VerbForm
          register={register}
          verb="handling"
          row={row}
          siblings={siblings}
          actor={actor}
          columns={columns}
          saveLabel={register === 'risk' ? 'Save treatment' : 'Save handling'}
          confirmNote={withdrawalNote}
          onSaved={onSaved}
        />
      ) : (
        <p className={styles.hint}>An improvement has no handling fields here: log its actions below, and add attachments on SharePoint.</p>
      )}
      <SiblingComposer row={row} actor={actor} onSaved={onSaved} />
    </div>
  );
}
